/**
 * 매출 분석 API 클라이언트 — backend /analysis/*.
 *
 * Phase 6-4: maintenance.
 * Phase 6-5: monthly + ranking (end-user / invoice-company / storage) + 통합 엑셀 export.
 */

import type {
  AnalysisFilterOptions,
  AnalysisMonthlyData,
  AnalysisYearlyData,
  AnalysisRankingData,
  ApiResponse,
  MaintenanceData,
  WorklistData,
} from './types';

import { api } from './api';

const BASE = '/api';

/** 월별매출분석 필터 — 법인(company)·담당자(emp). 미지정(전체)이면 빈 문자열/undefined. */
export type MonthlyFilter = { company?: 'SM' | 'EJ' | ''; emp?: string };

function filterQuery(filter?: MonthlyFilter): string {
  const params: string[] = [];
  if (filter?.company) params.push(`company=${encodeURIComponent(filter.company)}`);
  if (filter?.emp) params.push(`emp=${encodeURIComponent(filter.emp)}`);
  return params.length ? `&${params.join('&')}` : '';
}

export async function getMaintenance(year: number): Promise<MaintenanceData> {
  const res = await fetch(`${BASE}/analysis/maintenance?year=${year}`, {
    credentials: 'include',
  });
  const json = (await res.json()) as ApiResponse<MaintenanceData>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

/** 탭1 월별매출분석 (법인·담당자 필터) */
export async function getMonthlyAnalysis(
  year: number,
  filter?: MonthlyFilter,
): Promise<AnalysisMonthlyData> {
  const res = await fetch(`${BASE}/analysis/monthly?year=${year}${filterQuery(filter)}`, {
    credentials: 'include',
  });
  const json = (await res.json()) as ApiResponse<AnalysisMonthlyData>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

/* ── 월별 금액 드릴다운 (v2.79.0) — ① 표의 영업건·유지보수·비상벨 칸 클릭 ── */

export type MonthlyDetailKind = 'sales' | 'maint' | 'bell';

export interface MonthlyDetailSalesRow {
  salesId: string;
  endUser: string | null;
  projectName: string | null;
  amount: number;
  invoiceDate: string;
  status: string;
  empName: string | null;
}
export interface MonthlyDetailMaintRow {
  endUser: string | null;
  contractCompany: string | null;
  amount: number;
  invoiceDate: string;
  /** 몇 월분인지 (발행월과 다를 수 있음 — 12월분이 1월 발행 등) */
  forMonth: string | null;
}
export interface MonthlyDetailBellRow {
  siteName: string | null;
  endUser: string | null;
  amount: number;
  invoiceDate: string;
  deviceCount: number | null;
}

export interface MonthlyDetail {
  kind: MonthlyDetailKind;
  year: number;
  month: number;
  rows: (MonthlyDetailSalesRow | MonthlyDetailMaintRow | MonthlyDetailBellRow)[];
}

/** ① 표의 한 칸(월×유형)을 이루는 건들 — 화면과 같은 조건으로 서버가 뽑는다 */
export async function getMonthlyDetail(
  year: number,
  month: number,
  kind: MonthlyDetailKind,
  filter?: MonthlyFilter,
): Promise<MonthlyDetail> {
  const res = await fetch(
    `${BASE}/analysis/monthly-detail?year=${year}&month=${month}&kind=${kind}${filterQuery(filter)}`,
    { credentials: 'include' },
  );
  const json = (await res.json()) as ApiResponse<MonthlyDetail>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

/** 연도별 매출분석 (최근 5개년 · 회사/유형 구분 · 손익 · 법인/담당자 필터) */
export async function getYearlyAnalysis(
  year: number,
  filter?: MonthlyFilter,
): Promise<AnalysisYearlyData> {
  const res = await fetch(`${BASE}/analysis/yearly?year=${year}${filterQuery(filter)}`, {
    credentials: 'include',
  });
  const json = (await res.json()) as ApiResponse<AnalysisYearlyData>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

/** 월별매출분석 필터 옵션 (법인·담당자 드롭다운 목록) */
export async function getFilterOptions(): Promise<AnalysisFilterOptions> {
  const res = await fetch(`${BASE}/analysis/filter-options`, { credentials: 'include' });
  const json = (await res.json()) as ApiResponse<AnalysisFilterOptions>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

/** 탭2 엔드유저별 매출액 (TOP N) */
export async function getEndUserRanking(
  year: number,
  top = 15,
): Promise<AnalysisRankingData> {
  const res = await fetch(
    `${BASE}/analysis/end-user?year=${year}&top=${top}`,
    { credentials: 'include' },
  );
  const json = (await res.json()) as ApiResponse<AnalysisRankingData>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

/** 탭3 거래처별 (영업=제안업체 기준) 매출액 (TOP N). API 경로는 호환상 invoice-company 유지. */
export async function getInvoiceCompanyRanking(
  year: number,
  top = 15,
): Promise<AnalysisRankingData> {
  const res = await fetch(
    `${BASE}/analysis/invoice-company?year=${year}&top=${top}`,
    { credentials: 'include' },
  );
  const json = (await res.json()) as ApiResponse<AnalysisRankingData>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

/** 탭3 거래처별 — 계산서 발행처(원청) 기준 (TOP N). 기준 토글용 (2026-07-11) */
export async function getBillingCompanyRanking(
  year: number,
  top = 15,
): Promise<AnalysisRankingData> {
  const res = await fetch(
    `${BASE}/analysis/billing-company?year=${year}&top=${top}`,
    { credentials: 'include' },
  );
  const json = (await res.json()) as ApiResponse<AnalysisRankingData>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

/** 미발행·수주잔고 워크리스트 (KPI 카드 드릴다운) */
export async function getWorklist(): Promise<WorklistData> {
  const res = await fetch(`${BASE}/analysis/worklist`, { credentials: 'include' });
  const json = (await res.json()) as ApiResponse<WorklistData>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

/** 워크리스트 엑셀 (미발행+수주잔고 2시트) — sales+ */
export async function downloadWorklistExcel(): Promise<void> {
  const res = await fetch(`${BASE}/analysis/worklist/export`, { credentials: 'include' });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const json = (await res.json()) as { error?: { message?: string } };
      if (json?.error?.message) msg = json.error.message;
    } catch {
      /* not JSON */
    }
    throw new Error(msg);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const cd = res.headers.get('Content-Disposition') ?? '';
  const utf8Match = cd.match(/filename\*=UTF-8''([^;]+)/i);
  a.download = utf8Match ? decodeURIComponent(utf8Match[1]!) : 'worklist.xlsx';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** 탭4 엔드유저별 스토리지 TB (TOP N) */
export async function getStorageRanking(
  year: number,
  top = 20,
): Promise<AnalysisRankingData> {
  const res = await fetch(
    `${BASE}/analysis/storage?year=${year}&top=${top}`,
    { credentials: 'include' },
  );
  const json = (await res.json()) as ApiResponse<AnalysisRankingData>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

/** 📄 영업팀 월간보고서 — 작성자 정보 (소속·직급·성명, 모달에서 입력·기억) */
export interface MonthlyReportAuthor {
  dept: string;
  rank: string;
  name: string;
}

/** 📄 영업팀 월간보고서 미리보기 HTML — sales+. 전사 기준. notes = 기타 특이사항(줄 단위) */
export async function previewMonthlyReport(
  year: number,
  month: number,
  notes: string,
  author: MonthlyReportAuthor,
): Promise<string> {
  const res = await fetch(`${BASE}/analysis/monthly-report/preview`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ year, month, notes, author }),
  });
  const json = (await res.json()) as ApiResponse<{ html: string }>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data.html;
}

/**
 * 📄 영업팀 월간보고서 PDF 생성 — sales+. 전사 기준. 특이사항·작성자 반영.
 * variant='print' = A4 인쇄용 (결재판 포함) · 'slides' = 회의실 화면용 16:9 발표 슬라이드.
 * 두 판은 **같은 데이터**로 만든다 — 배치만 다르다.
 */
export async function downloadMonthlyReport(
  year: number,
  month: number,
  notes: string,
  author: MonthlyReportAuthor,
  variant: 'print' | 'slides' = 'print',
): Promise<void> {
  const path = variant === 'slides' ? '/analysis/monthly-report/slides' : '/analysis/monthly-report';
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ year, month, notes, author }),
  });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const json = (await res.json()) as { error?: { message?: string } };
      if (json?.error?.message) msg = json.error.message;
    } catch {
      /* not JSON */
    }
    throw new Error(msg);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const cd = res.headers.get('Content-Disposition') ?? '';
  const utf8Match = cd.match(/filename\*=UTF-8''([^;]+)/i);
  a.download = utf8Match
    ? decodeURIComponent(utf8Match[1]!)
    : `monthly_report_${variant}_${year}_${month}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** 매출분석 통합 엑셀 (4시트) — sales+admin. 월별 시트는 법인·담당자 필터 반영 */
export async function downloadAnalysisExcel(
  year: number,
  filter?: MonthlyFilter,
): Promise<void> {
  const res = await fetch(`${BASE}/analysis/export?year=${year}${filterQuery(filter)}`, {
    credentials: 'include',
  });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const json = (await res.json()) as { error?: { message?: string } };
      if (json?.error?.message) msg = json.error.message;
    } catch {
      /* not JSON */
    }
    throw new Error(msg);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const cd = res.headers.get('Content-Disposition') ?? '';
  const utf8Match = cd.match(/filename\*=UTF-8''([^;]+)/i);
  const asciiMatch = cd.match(/filename="([^"]+)"/i);
  const name = utf8Match
    ? decodeURIComponent(utf8Match[1]!)
    : asciiMatch
      ? asciiMatch[1]!
      : `analysis_${year}.xlsx`;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** ①-1 기타비용 상세 (v1.5.0) — 귀속월(발행월) + 발행 전 건 구분 */
export async function getMiscCosts(
  year: number,
  filter?: MonthlyFilter,
): Promise<{ year: number; rows: import('./types').AnalysisMiscCostRow[] }> {
  const res = await fetch(`${BASE}/analysis/misc-costs?year=${year}${filterQuery(filter)}`, {
    credentials: 'include',
  });
  const json = (await res.json()) as ApiResponse<{
    year: number;
    rows: import('./types').AnalysisMiscCostRow[];
  }>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

/* ══════════════════════════════════════════════════════════════
 * ⑤ 수금 (2026-08-24, v2.85.0 예정 · v2.87.0 실입금 4축)
 *
 * 월별 4축 = 발행(발행일) · 수금(들어왔으면 입금월·아니면 예정월) · 완료(입금일) · 미수.
 * ⭐수금예정금액 = **수금완료금액 + 미수금** (한 건은 한 달에만 선다 — 서버 eff_date).
 * 예정일 = 지정값 우선, 없으면 규칙(발행일 익월 말일). 단 **이미 입금된 건은 입금일이 이긴다**.
 * 입금일 = 대장 엑셀 업로드 매칭 또는 ✎ 직접 지정 (비상벨은 원본 paid_date 폴백).
 * ⚠되돌리기는 **본문 없는 DELETE** — api.delete 헬퍼가 body 없으면 Content-Type 을
 *   붙이지 않는다(붙이면 Fastify 가 라우트 전에 400 을 내며 말없이 먹통이 된다).
 * ══════════════════════════════════════════════════════════════ */

export type CollectionKind = 'sales' | 'maint' | 'bell';

/**
 * 드릴다운 축 — 어느 숫자를 눌렀나.
 *   issued 발행월 / unrecovered 발행월 중 미입금(미회수) /
 *   due **예정일 기준 계획** / paid 입금월 /
 *   out 미수금 / overdue 미수 중 예정일 지남 / notdue 미수 중 아직 기한 전
 */
export type CollectionAxis =
  | 'issued' | 'unrecovered' | 'due' | 'paid' | 'out' | 'overdue' | 'notdue';

/**
 * 범위 — `year` 그 해 그 달 / `current` **연도·월과 무관한 오늘 기준 잔액**.
 * 미수금 카드(현재 미수금 · 연체 · 기한 전)가 'current' 로 연다.
 */
export type CollectionScope = 'year' | 'current';

export interface CollectionMonth {
  m: number;
  issuedTotal: number;     // 그 달 발행한 계산서 합계 (VAT 포함)
  issuedCnt: number;
  /** ⭐미회수 — 그 달 **발행분** 중 아직 입금 없음 (발행금액 = 회수 + 미회수) */
  unrecoveredTotal: number;
  unrecoveredCnt: number;
  dueTotal: number;        // 그 달 수금 합계 (= paidTotal + outTotal · eff_date 기준)
  dueCnt: number;
  overriddenCnt: number;   // 예정일을 직접 지정한 건수
  paidTotal: number;       // 그 달 실제 입금된 합계
  paidCnt: number;
  // 수금완료 통로별 (영업건/유지보수/비상벨). ⚠2026-08-25 부터 **화면에는 안 쓴다**(칸 제거 요청) —
  //   서버는 계속 내려주고 `verify:collection` 이 「통로별 합 = 수금완료 합」 검산에 쓴다.
  paidSales: number;
  paidMaint: number;
  paidBell: number;
  outTotal: number;        // 미수금 — 그 달 예정인데 아직 입금 없음
  outCnt: number;
  /** ⭐연체 — 미수금 중 **예정일이 지난** 것 (나머지는 아직 기한 전인 정상 채권) */
  overdueTotal: number;
  overdueCnt: number;
}

export interface CollectionRule {
  label: string;
  detail: string;
  note: string;
  /** 수금 관리 시작일 — 이 날짜 이전 발행분은 아예 안 센다 (YYYY-MM-DD) */
  startDate: string;
  /** 그 범위를 사람 말로 (화면 안내문이 엔진과 어긋나지 않게 서버가 낸다) */
  scope: string;
}

export interface CollectionData {
  year: number;
  months: CollectionMonth[];
  totals: {
    issuedTotal: number; issuedCnt: number;
    unrecoveredTotal: number; unrecoveredCnt: number;
    dueTotal: number; dueCnt: number; overriddenCnt: number;
    paidTotal: number; paidCnt: number;
    paidSales: number; paidMaint: number; paidBell: number;
    outTotal: number; outCnt: number;
    overdueTotal: number; overdueCnt: number;
  };
  /**
   * ⭐오늘 기준 잔액 — **연도와 무관**. 미수금은 「그 달에 일어난 일」이 아니라 지금의 잔액이라
   * 달력에 늘어놓지 않고 화면 맨 위 카드 하나로 낸다 (2026-08-25 · v2.92.0).
   *   미수금 = 연체(예정일 지남) + 기한 전
   */
  current: {
    outTotal: number; outCnt: number;
    overdueTotal: number; overdueCnt: number;
    notDueTotal: number; notDueCnt: number;
  };
  rule: CollectionRule;
}

export interface CollectionDetailRow {
  kind: CollectionKind;
  refId: string;
  dueDate: string;
  overridden: boolean;
  /** 'request' 발행요청에서 입력한 값 · 'manual' 표에서 직접 지정 · null 규칙값 */
  sourceKind: 'request' | 'manual' | null;
  dueReason: string | null;
  invoiceDate: string;
  supply: number;
  vat: number;
  total: number;
  /** 실제 입금일 — null 이면 미수금 */
  paidDate: string | null;
  paidOverridden: boolean;
  /** 'excel' 대장 업로드 · 'manual' 직접 지정 · 'bell' 비상벨 원본값 · null 미수금 */
  paidSource: 'excel' | 'manual' | 'bell' | null;
  paidReason: string | null;
  /** 계산서 발행내역 — 대장 「세금계산서 발행품목」 원문(월이 들어 있다). 없으면 null → 건명 폴백 */
  invoiceItem: string | null;
  /** 이 건이 선 달 — 입금됐으면 입금일, 아니면 예정일 */
  effDate: string;
  payer: string | null;    // 돈 주는 곳 (채권처)
  endUser: string | null;
  title: string | null;
  companyId: string | null;
  empName: string | null;
}

export interface CollectionDetail {
  year: number;
  month: number;
  axis: CollectionAxis;
  /** 통로별 칸을 눌렀으면 그 통로 (없으면 전체) */
  kind: CollectionKind | null;
  rows: CollectionDetailRow[];
}

/** ⑤ 월별 수금 (발행·예정·완료·미수 4축) */
export async function getCollection(
  year: number,
  filter?: MonthlyFilter,
): Promise<CollectionData> {
  const res = await fetch(`${BASE}/analysis/collection?year=${year}${filterQuery(filter)}`, {
    credentials: 'include',
  });
  const json = (await res.json()) as ApiResponse<CollectionData>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

/** 그 달의 수금을 이루는 건들 — 표의 칸과 같은 조건으로 서버가 뽑는다 (axis = 누른 칸) */
export async function getCollectionDetail(
  year: number,
  month: number,
  axis: CollectionAxis,
  filter?: MonthlyFilter,
  kind?: CollectionKind,
  scope: CollectionScope = 'year',
): Promise<CollectionDetail> {
  const res = await fetch(
    `${BASE}/analysis/collection-detail?year=${year}&month=${month}&axis=${axis}` +
      `${scope === 'current' ? '&scope=current' : ''}` +
      `${kind ? `&kind=${kind}` : ''}${filterQuery(filter)}`,
    { credentials: 'include' },
  );
  const json = (await res.json()) as ApiResponse<CollectionDetail>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

/** ✎ 예정일 지정 — 규칙값을 이긴다 */
export async function setCollectionDue(
  kind: CollectionKind,
  refId: string,
  dueDate: string,
  reason?: string,
): Promise<void> {
  await api.put('/analysis/collection/due', { kind, refId, dueDate, reason: reason ?? '' });
}

/** ↩ 되돌리기 — 지정을 지우면 규칙값으로 복귀 */
export async function clearCollectionDue(kind: CollectionKind, refId: string): Promise<void> {
  await api.delete(
    `/analysis/collection/due?kind=${kind}&refId=${encodeURIComponent(refId)}`,
  );
}

/** ✎ 입금일 지정 — 사람 판단이 엑셀 매칭을 이긴다 (v2.87.0) */
export async function setCollectionPaid(
  kind: CollectionKind,
  refId: string,
  paidDate: string,
  reason?: string,
): Promise<void> {
  await api.put('/analysis/collection/paid', { kind, refId, paidDate, reason: reason ?? '' });
}

/** ↩ 입금일 되돌리기 — 미수금으로 복귀 (비상벨은 원본값이 다시 보인다) */
export async function clearCollectionPaid(kind: CollectionKind, refId: string): Promise<void> {
  await api.delete(
    `/analysis/collection/paid?kind=${kind}&refId=${encodeURIComponent(refId)}`,
  );
}

/* ── 🏦 대장 엑셀 업로드 (매출매입관리대장 [매출세금계산서] 시트) ── */

/**
 * 대장의 법인 — 대장은 회사마다 따로다.
 *   SM = 「1. 에스엠_매출매입관리대장_YYYY」 · EJ = 「이진아이씨티_매입매출내역_YYYY」
 * ⚠파일 안에는 회사 표시가 없다(두 파일의 시트·머리글이 같다) → 올릴 때 사람이 고른다.
 */
export type CollectionCompany = 'SM' | 'EJ';

/** 법인 표시명 — 화면 어디서나 같은 말을 쓰도록 여기 한 곳에서 낸다. */
export const COLLECTION_COMPANY_LABEL: Record<CollectionCompany, string> = {
  SM: '에스엠',
  EJ: '이진ICT',
};

export interface BankLedgerRow {
  issueDate: string;
  company: string;
  supply: number;
  total: number;
  deposit: string | null;
  depositText: string | null;
  serial: string | null;
  title: string;
  /** 「세금계산서 발행품목」 — 월이 들어 있는 원문 (없으면 「납품내역」) */
  item: string;
}

export type BankPlanStatus =
  | 'set_paid' | 'update_paid' | 'clear_paid' | 'skip_done' | 'unpaid_ok'
  | 'kept_manual' | 'unpaid_conflict' | 'amount_mismatch' | 'ambiguous'
  | 'no_match' | 'revision_skip' | 'no_deposit';

export interface BankImportResultRow {
  issueDate: string;
  company: string;
  title: string;
  supply: number;
  total: number;
  deposit: string | null;
  depositText: string | null;
  serial: string | null;
  status: BankPlanStatus;
  via: 'serial' | 'exact' | 'group' | 'split' | null;
  note: string | null;
  target: { kind: CollectionKind; refId: string; payer: string | null; title: string | null } | null;
}

export interface BankImportResult {
  applied: boolean;
  appliedCount?: number;
  /** 계산서 발행내역을 넣거나 갱신한 건수 */
  itemsSaved?: number;
  /** 올린 대장의 법인 (적용 응답에만) */
  companyId?: CollectionCompany;
  summary: {
    total: number;
    setPaid: number; updatePaid: number; clearPaid: number;
    skipDone: number; unpaidOk: number; keptManual: number;
    needReview: number; noMatch: number; revisionSkip: number; noDeposit: number;
    /** 고른 법인과 **다른** 법인의 건에 붙은 행 수 — 막지 않고 알려만 준다 */
    otherCompany: number;
  };
  rows: BankImportResultRow[];
}

/** 대장 매칭 — apply=false 미리보기(쓰기 없음) → apply=true 적용 */
export async function bankImport(
  rows: BankLedgerRow[],
  apply: boolean,
  companyId: CollectionCompany,
): Promise<BankImportResult> {
  return api.post<BankImportResult>('/analysis/collection/bank-import', { rows, apply, companyId });
}

/* ── 📜 대장 업로드 이력 (누가 언제 올렸나) ── */

export interface BankUploadLog {
  logId: number;
  year: number;
  /** 어느 법인 대장인가 — 칸이 생기기 전(2026-08-25) 이력은 전부 'SM' 으로 채워져 있다 */
  companyId: CollectionCompany | null;
  uploadedBy: string | null;
  uploadedName: string | null;
  uploadedAt: string;
  rowCount: number;
  matchedCount: number;
  appliedCount: number;
  itemsCount: number;
  reviewCount: number;
  skippedCount: number;
}

export async function getBankUploads(year?: number): Promise<{ rows: BankUploadLog[] }> {
  const q = year ? `?year=${year}` : '';
  return api.get<{ rows: BankUploadLog[] }>(`/analysis/collection/uploads${q}`);
}

/* ── 🚨 장기미수 (v2.327.0 · 개발요청 #39 · backend lib/collection/long-overdue.ts) ── */

export interface DelayNote {
  noteId: number;
  reason: string;
  /** 바꾼 수금 예정일(적었을 때만) · 그때까지의 예정일 */
  newDueDate: string | null;
  prevDueDate: string | null;
  /** 적은 때의 미수 금액(VAT 포함) */
  amountAtNote: number | null;
  createdBy: string;
  createdByName: string;
  /** 한국 시간 'YYYY-MM-DD HH:MI' */
  createdAt: string;
  /** 경영지원부 알림 결과 한 줄 */
  noticeResult: string | null;
}

export interface LongOverdueItem {
  kind: CollectionKind;
  refId: string;
  title: string | null;
  endUser: string | null;
  payer: string | null;
  companyId: CollectionCompany | null;
  empId: string | null;
  empName: string | null;
  invoiceDate: string;
  dueDate: string;
  dueOverridden: boolean;
  /** 미수 금액 — 합계(VAT 포함) */
  total: number;
  daysOverdue: number;
  lastNote: DelayNote | null;
  noteCount: number;
  /** 내가 지연 사유를 적을 수 있나 — 담당 영업 · 경영지원부 지정 · 관리자(서버 판정) */
  canWrite: boolean;
}

export interface LongOverdueResult {
  today: string;
  /** 「장기」 기준 개월 */
  months: number;
  total: number;
  uploads: { companyId: string; date: string }[];
  rows: LongOverdueItem[];
}

export async function getLongOverdue(opts: { company?: string; emp?: string } = {}): Promise<LongOverdueResult> {
  const q = new URLSearchParams();
  if (opts.company) q.set('company', opts.company);
  if (opts.emp) q.set('emp', opts.emp);
  const s = q.toString();
  return api.get<LongOverdueResult>(`/analysis/collection/long-overdue${s ? `?${s}` : ''}`);
}

export async function getDelayNotes(kind: CollectionKind, refId: string): Promise<{ notes: DelayNote[] }> {
  return api.get<{ notes: DelayNote[] }>(`/analysis/collection/delay-notes?kind=${kind}&refId=${encodeURIComponent(refId)}`);
}

/** 지연 사유 적기 — 바뀐 예정일('' = 안 바꿈)은 결제관리 예정일에도 들어간다 · 적으면 경영지원부에 포털챗봇 알림 */
export async function addDelayNote(
  kind: CollectionKind, refId: string, reason: string, newDueDate: string,
): Promise<{ note: DelayNote; noticeResult: string }> {
  return api.post<{ note: DelayNote; noticeResult: string }>('/analysis/collection/delay-notes', { kind, refId, reason, newDueDate });
}
